// @flow
import type {Node} from 'react'
import React, {useState, useEffect, useCallback} from 'react'
import classNames from 'classnames'
import orgNavigation from './orgNavigation'

import styles from './index.scss'

type Props = {
  activeClassName?: string,
  children: Node,
  className?: ?string,
  dataTurboFrame?: ?string,
  disabled?: boolean,
  highlightActive?: boolean,
  href: string,
  onClick?: ?(*) => any,
  target?: ?string,
  title?: string,
  disableSmartNavigation?: boolean,
  ...
}

const AppLink = ({
  activeClassName,
  children,
  dataTurboFrame,
  disabled = false,
  highlightActive = false,
  href,
  onClick,
  title,
  className,
  disableSmartNavigation,
  ...restLinkProps
}: Props): Node => {
  const [currentPathname, setCurrentPathname] = useState(window.location.pathname)

  const handleLocationChange = useCallback(() => {
    setCurrentPathname(window.location.pathname)
  }, [])

  useEffect(() => {
    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange)

    // Listen for pushstate/replacestate events (programmatic navigation)
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = (...args) => {
      originalPushState.apply(window.history, args)
      handleLocationChange()
    }

    window.history.replaceState = (...args) => {
      originalReplaceState.apply(window.history, args)
      handleLocationChange()
    }

    return () => {
      window.removeEventListener('popstate', handleLocationChange)
      // Note: We don't restore original pushState/replaceState to avoid conflicts
      // with other components that might also be patching them
    }
  }, [handleLocationChange])

  const isActive = highlightActive && currentPathname === href

  const linkClassName = classNames(
    className || '',
    isActive && activeClassName && activeClassName,
  )

  const smartNavigate = useCallback((event: SyntheticMouseEvent<HTMLAnchorElement>) => {
    const shouldUseReactRouter = !disableSmartNavigation && orgNavigation.shouldUseReactRouter(href)

    if (shouldUseReactRouter) {
      const {currentTarget, button, metaKey, ctrlKey, shiftKey} = event
      const targetAttr = currentTarget.getAttribute('target') || ''
      const isAuxiliaryClick = typeof button === 'number' && button !== 0
      const isModifiedClick = metaKey || ctrlKey || shiftKey || isAuxiliaryClick
      const opensInNewContext = targetAttr && targetAttr.toLowerCase() !== '_self'

      if (!(isModifiedClick || opensInNewContext)) {
        orgNavigation.navigateViaReactRouter(href, () => event.preventDefault())
        return
      }
    }

    // Call original onClick handler if provided
    if (onClick)
      onClick(event)

    // Let browser handle normal navigation (Turbo will pick it up)
  }, [href, onClick, disableSmartNavigation])

  if (disabled) {
    const buttonClass = classNames(styles.disabledButton, className)
    return (
      <button
        className={buttonClass}
        type="button"
        disabled
      >
        {children}
      </button>
    )
  }

  return (
    <a
      {...restLinkProps}
      className={linkClassName}
      href={href}
      onClick={smartNavigate}
      data-turbo
      data-turbo-frame={dataTurboFrame || 'page-frame'}
      title={title || ''}
    >
      {children}
    </a>
  )
}

export default AppLink
